// @ts-check
import { db } from '@/core/config/firebase';
import { COLECCIONES, MAIN_DATA_DOC_ID } from '@/core/constants/collections';
import {
    doc,
    updateDoc,
    collection,
    serverTimestamp,
    addDoc,
    setDoc,
    Timestamp,
    DocumentData,
    getDoc,
    getDocs
} from 'firebase/firestore';

// Importar el store de inventario para enviar señales
import { publicInventarioStore } from '@/core/store/inventario.store';
// Importar el store de ventas para recargarlo después de cambios
import { ventasStore } from '@/core/store/ventas.store';

/**
 * @file src/core/services/ventas.service.ts
 * @description Servicio de datos para la página de ventas. Encapsula toda la lógica
 * de interacción con Firestore para ventas, clientes y configuraciones relacionadas.
 */

// --- Type Imports ---
import type {
    Venta,
    Cliente,
    UserProfile,
    ActividadCliente,
    EstadoPedidoProveedor,
} from '@/core/types';
import { generateId } from '@/core/utils/helpers';

/**
 * Recalcula los campos financieros de una venta basado en su estado.
 * Se modifica el objeto de venta directamente (por referencia).
 * @param {Partial<Venta>} venta - El objeto de la venta a recalcular.
 */
function recalculateSaleFinances(venta: Partial<Venta>): void {
    if (!venta || typeof venta.ventaTotal !== 'number') {
        if (venta) {
            venta.comisionCaex = 0;
            venta.fondosAcreditados = 0;
        }
        return;
    }

    // El 4% de comisión de Cargo Expreso se aplica solo a ventas en línea si la venta fue entregada o ya liquidada.
    const tipoVenta = venta.tipoVenta || 'linea'; // Por defecto 'linea' para compatibilidad con datos antiguos
    if (
        tipoVenta === 'linea' &&
        venta.modalidadAbastecimiento !== 'proveedor_directo' &&
        (venta.estadoPedido === 'Entregado' || venta.estadoPedido === 'Liquidado') &&
        venta.ventaTotal > 0
    ) {
        venta.comisionCaex = venta.ventaTotal * 0.04;
    } else {
        venta.comisionCaex = 0;
    }

    // Los fondos solo se acreditan cuando la venta está efectivamente liquidada.
    if (venta.estadoPedido === 'Liquidado' && venta.ventaTotal > 0) {
        const comision = typeof venta.comisionCaex === 'number' ? venta.comisionCaex : 0;
        venta.fondosAcreditados = venta.ventaTotal - comision;
    } else {
        venta.fondosAcreditados = 0;
    }
}


/**
 * Obtiene los datos de ventas una sola vez.
 * Optimizado para minimizar lecturas: carga todas las ventas del documento (1 lectura)
 * y luego limita a 20 registros inicialmente para reducir el procesamiento inicial.
 * @param {string} userId - ID del usuario autenticado.
 * @param {number} limit - Número máximo de registros a retornar (por defecto 20 para optimización).
 * @returns {Promise<Venta[]>} Un array con los datos de ventas limitado al parámetro limit.
 */
export async function getSales(userId: string, limit?: number): Promise<Venta[]> {
    const mainDataPath = `${COLECCIONES.USUARIOS}/${userId}/${COLECCIONES.APP_DATA}/${MAIN_DATA_DOC_ID}`;
    const mainDataRef = doc(db, mainDataPath);
    const docSnap = await getDoc(mainDataRef);
    if (docSnap.exists()) {
        const data = docSnap.data() as DocumentData;
        const todasLasVentas = (data.ventasData || []) as Venta[];
        
        // 🛠️ MIGRACIÓN AUTOMÁTICA EN SEGUNDO PLANO DE FECHAS ANTERIORES (DD/MM/YYYY -> YYYY-MM-DD)
        let seHicieronCambios = false;
        const ventasCorregidas = todasLasVentas.map(venta => {
            if (venta.fecha && venta.fecha.includes('/')) {
                const parts = venta.fecha.split('/');
                if (parts.length === 3) {
                    const dia = parts[0].padStart(2, '0');
                    const mes = parts[1].padStart(2, '0');
                    const anio = parts[2];
                    if (anio.length === 4 && dia.length === 2 && mes.length === 2) {
                        seHicieronCambios = true;
                        return {
                            ...venta,
                            fecha: `${anio}-${mes}-${dia}`
                        };
                    }
                }
            }
            return venta;
        });

        if (seHicieronCambios) {
            console.log("[VentasService] 🛠️ Detectadas ventas con fecha obsoleta DD/MM/YYYY. Corrigiendo y guardando en Firestore...");
            // Guardar el array corregido de forma asíncrona en segundo plano sin retrasar el hilo principal
            updateDoc(mainDataRef, { ventasData: ventasCorregidas })
                .then(() => console.log("[VentasService] ✅ Ventas obsoletas migradas exitosamente a formato YYYY-MM-DD en Firestore."))
                .catch(err => console.error("[VentasService] ❌ Error al guardar la migración de fechas:", err));
            
            if (limit && limit > 0) {
                return ventasCorregidas.slice(0, limit);
            }
            return ventasCorregidas;
        }
        
        // Si se especifica un límite, retornar solo ese número de registros
        // Esto optimiza la carga inicial mostrando solo 20 registros
        if (limit && limit > 0) {
            return todasLasVentas.slice(0, limit);
        }
        
        return todasLasVentas;
    }
    console.warn("El documento mainData no existe al obtener ventas. Devolviendo array vacío.");
    return [];
}

/**
 * Obtiene los datos de la colección de clientes una sola vez.
 * @param {string} userId - ID del usuario autenticado.
 * @returns {Promise<Cliente[]>} Una lista con los clientes.
 */
export async function getClients(userId: string): Promise<Cliente[]> {
    const clientesPath = `${COLECCIONES.USUARIOS}/${userId}/${COLECCIONES.CLIENTES}`;
    const clientesRef = collection(db, clientesPath);
    const snapshot = await getDocs(clientesRef);
    return snapshot.docs.map(doc => ({ ...doc.data(), id: doc.id } as Cliente));
}

/**
 * Guarda (crea o actualiza) una venta.
 * @param {string} userId - ID del usuario.
 * @param {Partial<Venta>} ventaData - Objeto con los datos de la venta.
 * @returns {Promise<Venta>} La venta creada o actualizada.
 */
export async function saveVenta(userId: string, ventaData: Partial<Venta>): Promise<Venta> {
    const mainDataRef = doc(db, `${COLECCIONES.USUARIOS}/${userId}/${COLECCIONES.APP_DATA}/${MAIN_DATA_DOC_ID}`);

    // Leemos el estado actual de las ventas una sola vez para evitar race conditions.
    const docSnap = await getDoc(mainDataRef);
    const ventasActuales = docSnap.exists() ? (docSnap.data().ventasData as Venta[] || []) : [];

    let newVentasArray = [...ventasActuales];
    let ventaResultante: Venta;

    recalculateSaleFinances(ventaData);

    if (ventaData.id) { // Actualizar venta existente
        const index = newVentasArray.findIndex(v => v.id === ventaData.id);
        if (index > -1) {
            const ventaOriginal = newVentasArray[index];
            ventaResultante = {
                ...ventaOriginal,
                ...ventaData,
                timestampActualizacion: Timestamp.now()
            } as Venta;
            if (ventaResultante.modalidadAbastecimiento !== 'proveedor_directo') {
                delete ventaResultante.proveedorVenta;
                delete ventaResultante.estadoPedidoProveedor;
                delete ventaResultante.referenciaPedidoProveedor;
                delete ventaResultante.trackingProveedor;
                delete ventaResultante.fechaPedidoProveedor;
            }
            newVentasArray[index] = ventaResultante;
        } else {
            throw new Error(`No se encontró la venta con ID ${ventaData.id} para actualizar.`);
        }
    } else { // Crear nueva venta
        const nuevaVenta: Venta = {
            ...(ventaData as Omit<Venta, 'id' | 'timestamp' | 'timestampActualizacion'>),
            id: generateId('venta'),
            timestamp: Timestamp.now(),
            timestampActualizacion: Timestamp.now()
        };
        ventaResultante = nuevaVenta;
        newVentasArray.unshift(ventaResultante); // Agrega la nueva venta al principio
    }

    await updateDoc(mainDataRef, { ventasData: newVentasArray });

    // 🤖 AUTOMATIZACIÓN CRM WHATSAPP: Cambiar etiqueta a "Comprador" al registrar pedido
    if (ventaData.telefono) {
        const normalizedPhone = ventaData.telefono.replace(/[^\d]/g, "");
        if (normalizedPhone.length >= 8) {
            try {
                const chatRef = doc(db, `usuarios/${userId}/whatsapp_chats/${normalizedPhone}`);
                const chatSnap = await getDoc(chatRef);
                if (chatSnap.exists()) {
                    const currentLabel = chatSnap.data()?.clientLabel;
                    
                    // Protegemos el flujo logístico activo para que no se salte etapas
                    const enFlujoLogistico = ["Nuevo Pedido", "Recolección Activa", "Recolección Reserva", "Pedido Reservado", "Pedido Procesado", "Pedido Recolectado"].includes(currentLabel);
                    
                    if (!enFlujoLogistico) {
                        await updateDoc(chatRef, {
                            clientLabel: "Comprador",
                            updatedAt: serverTimestamp()
                        });
                        console.log(`[Automation] Chat ${normalizedPhone} cambiado automáticamente a "Comprador" por registro de pedido.`);
                    } else {
                        console.log(`[Automation] Chat ${normalizedPhone} está en flujo logístico (${currentLabel}). Se conserva su estado logístico intacto.`);
                    }
                }
            } catch (err) {
                console.error("[Automation] Error al actualizar estado de WhatsApp a Comprador:", err);
            }
        }
    }

    // 🔄 SEÑAL: Marcar inventario para actualización después de guardar venta
    console.log('[VentasService] 🔄 Marcando inventario para actualización después de guardar venta');
    publicInventarioStore.markForUpdate();

    // 🔄 RECARGA: Recargar store de ventas para tener datos actualizados
    console.log('[VentasService] 🔄 Recargando store de ventas después de guardar');
    await ventasStore.reload();

    return ventaResultante;
}

/**
 * Elimina una venta del array de ventas.
 * @param {string} userId - ID del usuario.
 * @param {string} ventaId - ID de la venta a eliminar.
 * @returns {Promise<void>}
 */
export async function deleteVenta(userId: string, ventaId: string): Promise<void> {
    const mainDataRef = doc(db, `${COLECCIONES.USUARIOS}/${userId}/${COLECCIONES.APP_DATA}/${MAIN_DATA_DOC_ID}`);
    const docSnap = await getDoc(mainDataRef);
    if (!docSnap.exists()) throw new Error("Documento principal no encontrado.");

    const ventasActuales = docSnap.data().ventasData as Venta[] || [];
    const updatedVentasArray = ventasActuales.filter(v => v.id !== ventaId);

    await updateDoc(mainDataRef, { ventasData: updatedVentasArray });

    // 🔄 SEÑAL: Marcar inventario para actualización después de eliminar venta
    console.log('[VentasService] 🔄 Marcando inventario para actualización después de eliminar venta');
    publicInventarioStore.markForUpdate();

    // 🔄 RECARGA: Recargar store de ventas para tener datos actualizados
    console.log('[VentasService] 🔄 Recargando store de ventas después de eliminar');
    await ventasStore.reload();
}

/**
 * Actualiza solo el estado de un pedido y recalcula sus finanzas.
 * @param {string} userId - ID del usuario.
 * @param {string} ventaId - ID de la venta a actualizar.
 * @param {string} nuevoEstado - El nuevo estado del pedido.
 * @returns {Promise<Venta | null>} La venta actualizada, o null si no se encontró.
 */
export async function updateVentaStatus(userId: string, ventaId: string, nuevoEstado: string): Promise<Venta | null> {
    const mainDataRef = doc(db, `${COLECCIONES.USUARIOS}/${userId}/${COLECCIONES.APP_DATA}/${MAIN_DATA_DOC_ID}`);
    const docSnap = await getDoc(mainDataRef);
    if (!docSnap.exists()) throw new Error("Documento principal no encontrado.");

    const ventasActuales = docSnap.data().ventasData as Venta[] || [];
    let ventaActualizada: Venta | null = null;

    const newVentasArray = ventasActuales.map(venta => {
        if (venta.id === ventaId) {
            const updatedVenta: Venta = {
                ...venta,
                estadoPedido: nuevoEstado,
                timestampActualizacion: Timestamp.now()
            };
            recalculateSaleFinances(updatedVenta);
            ventaActualizada = updatedVenta;
            return updatedVenta;
        }
        return venta;
    });

    if (ventaActualizada) {
        await updateDoc(mainDataRef, { ventasData: newVentasArray });

        // 🔄 SEÑAL: Marcar inventario para actualización después de cambiar estado
        console.log(`[VentasService] 🔄 Marcando inventario para actualización después de cambiar estado a: ${nuevoEstado}`);
        publicInventarioStore.markForUpdate();

        // 🔄 RECARGA: Recargar store de ventas para tener datos actualizados
        console.log('[VentasService] 🔄 Recargando store de ventas después de cambiar estado');
        await ventasStore.reload();

        return ventaActualizada;
    }

    console.error("No se encontró la venta para actualizar el estado.");
    return null;
}

/**
 * Actualiza únicamente el seguimiento de compra y despacho del proveedor.
 * El estado de entrega/cobro del cliente se conserva de forma independiente.
 */
export async function updateVentaStatusProveedor(
    userId: string,
    ventaId: string,
    nuevoEstado: EstadoPedidoProveedor
): Promise<Venta | null> {
    const mainDataRef = doc(
        db,
        `${COLECCIONES.USUARIOS}/${userId}/${COLECCIONES.APP_DATA}/${MAIN_DATA_DOC_ID}`
    );
    const docSnap = await getDoc(mainDataRef);
    if (!docSnap.exists()) throw new Error('Documento principal no encontrado.');

    const ventasActuales = (docSnap.data().ventasData as Venta[]) || [];
    let ventaActualizada: Venta | null = null;
    const newVentasArray = ventasActuales.map((venta) => {
        if (venta.id !== ventaId) return venta;
        if (venta.modalidadAbastecimiento !== 'proveedor_directo') {
            throw new Error('Esta venta no corresponde a un producto enviado por proveedor.');
        }

        const actualizada: Venta = {
            ...venta,
            estadoPedidoProveedor: nuevoEstado,
            timestampActualizacion: Timestamp.now(),
            ...(nuevoEstado === 'pedido_realizado' && !venta.fechaPedidoProveedor
                ? { fechaPedidoProveedor: new Date().toISOString().slice(0, 10) }
                : {}),
        };
        ventaActualizada = actualizada;
        return actualizada;
    });

    if (!ventaActualizada) return null;

    await updateDoc(mainDataRef, { ventasData: newVentasArray });
    publicInventarioStore.markForUpdate();
    await ventasStore.reload();
    return ventaActualizada;
}

/**
 * Calcula el número de días hábiles completos (excluyendo domingos) entre dos fechas.
 * IMPORTANTE: El cambio ocurre cuando cambia el día (a medianoche), NO después de 24 horas.
 * Solo cuenta días completos. Si el estado se marcó hoy, retorna 0 (no avanza el mismo día).
 * Los domingos no cuentan (se saltan al lunes).
 * 
 * Ejemplo:
 * - Lunes 10:00 AM: Se marca "Recolectado" → timestampActualizacion = Lunes 10:00 AM
 * - Lunes 2:00 PM: Se ejecuta la función → Normaliza a Lunes 00:00 vs Lunes 00:00 → Retorna 0 (mismo día, no avanza)
 * - Martes 8:00 AM: Se ejecuta la función → Normaliza a Lunes 00:00 vs Martes 00:00 → Retorna 1 (cambió el día, avanza)
 * 
 * @param {Date} fechaInicio - Fecha cuando se marcó el estado (ej: cuando se marcó "Recolectado").
 * @param {Date} fechaFin - Fecha actual.
 * @returns {number} Número de días hábiles completos transcurridos (0 si es el mismo día).
 */
function calcularDiasHabiles(fechaInicio: Date, fechaFin: Date): number {
    // Normalizar ambas fechas a medianoche (00:00:00) para comparar solo días, no horas
    // Esto permite que el cambio ocurra tan pronto como cambie el día, no después de 24 horas
    const inicio = new Date(fechaInicio);
    inicio.setHours(0, 0, 0, 0);
    
    const fin = new Date(fechaFin);
    fin.setHours(0, 0, 0, 0);
    
    // Si es el mismo día, retornar 0 (no ha pasado ningún día hábil)
    // Ejemplo: Si se marcó "Recolectado" hoy a las 10:00 AM, y se ejecuta hoy a las 2:00 PM,
    // ambas fechas se normalizan a medianoche del mismo día → retorna 0 (no avanza)
    if (inicio.getTime() === fin.getTime()) {
        return 0;
    }
    
    // Si la fecha de inicio es mayor que la fecha fin, retornar 0 (error de datos)
    if (inicio.getTime() > fin.getTime()) {
        return 0;
    }
    
    let diasHabiles = 0;
    const fechaActual = new Date(inicio);
    // Empezar a contar desde el día siguiente al día de recolección
    fechaActual.setDate(fechaActual.getDate() + 1);
    
    // Contar días hábiles (excluyendo domingos) hasta la fecha fin
    while (fechaActual <= fin) {
        const diaSemana = fechaActual.getDay();
        // Excluir domingos (0) - los domingos no cuentan, se saltan al lunes
        if (diaSemana !== 0) {
            diasHabiles++;
        }
        fechaActual.setDate(fechaActual.getDate() + 1);
    }
    
    return diasHabiles;
}

/**
 * Obtiene el siguiente estado en la secuencia de estados de envío.
 * @param {string} estadoActual - Estado actual del pedido.
 * @returns {string | null} El siguiente estado, o null si no hay siguiente estado.
 */
function obtenerSiguienteEstado(estadoActual: string): string | null {
    const secuenciaEstados: Record<string, string> = {
        'Recolectado': '1# En Camino',
        '1# En Camino': '2# En Camino',
        '2# En Camino': '3# En Camino',
        // '3# En Camino' no tiene siguiente estado
        
        // Secuencia En Agencia
        '1# En Agencia': '2# En Agencia',
        '2# En Agencia': '3# En Agencia',
        '3# En Agencia': '4# En Agencia',
        '4# En Agencia': '5# En Agencia',
    };
    
    return secuenciaEstados[estadoActual] || null;
}

/**
 * Actualiza automáticamente los estados de los pedidos basándose en los días hábiles transcurridos.
 * Solo actualiza pedidos que están en estados de envío (Recolectado, 1# En Camino, 2# En Camino).
 * No avanza los domingos.
 * @param {string} userId - ID del usuario.
 * @returns {Promise<number>} Número de ventas actualizadas.
 */
export async function actualizarEstadosAutomaticos(userId: string): Promise<number> {
    const mainDataRef = doc(db, `${COLECCIONES.USUARIOS}/${userId}/${COLECCIONES.APP_DATA}/${MAIN_DATA_DOC_ID}`);
    const docSnap = await getDoc(mainDataRef);
    
    if (!docSnap.exists()) {
        console.log('[VentasService] No se encontró el documento principal.');
        return 0;
    }

    const ventasActuales = docSnap.data().ventasData as Venta[] || [];
    const ahora = new Date();
    let ventasActualizadas = 0;
    let hayCambios = false;

    const newVentasArray = ventasActuales.map(venta => {
        // Solo procesar ventas en línea con estados de envío
        const tipoVenta = venta.tipoVenta || 'linea';
        if (tipoVenta !== 'linea') {
            return venta;
        }

        let estadoActual = venta.estadoPedido;
        let ventaModificada = venta;
        
        // Migración de estados antiguos a nuevos nombres
        if (estadoActual === '1# Recolectado') {
            estadoActual = 'Recolectado';
            ventaModificada = {
                ...venta,
                estadoPedido: 'Recolectado',
                timestampActualizacion: Timestamp.now()
            };
            hayCambios = true;
        } else if (estadoActual === '4# En Camino') {
            estadoActual = '3# En Camino';
            ventaModificada = {
                ...venta,
                estadoPedido: '3# En Camino',
                timestampActualizacion: Timestamp.now()
            };
            hayCambios = true;
        }
        
        // Solo actualizar estados que están en la secuencia de envío o agencia
        const estadosConSiguiente = [
            'Recolectado', '1# En Camino', '2# En Camino',
            '1# En Agencia', '2# En Agencia', '3# En Agencia', '4# En Agencia'
        ];
        
        if (!estadosConSiguiente.includes(estadoActual)) {
            return ventaModificada;
        }

        // Si ya está en el último estado de la cadena, no avanzar más (redundante con el check anterior pero seguro)
        if (estadoActual === '3# En Camino' || estadoActual === '5# En Agencia') {
            return ventaModificada;
        }

        // Obtener fecha de última actualización
        let fechaUltimaActualizacion: Date;
        if (ventaModificada.timestampActualizacion) {
            if (ventaModificada.timestampActualizacion instanceof Date) {
                fechaUltimaActualizacion = ventaModificada.timestampActualizacion;
            } else {
                // Es un Timestamp de Firestore
                fechaUltimaActualizacion = ventaModificada.timestampActualizacion.toDate();
            }
        } else if (ventaModificada.timestamp) {
            if (ventaModificada.timestamp instanceof Date) {
                fechaUltimaActualizacion = ventaModificada.timestamp;
            } else {
                fechaUltimaActualizacion = ventaModificada.timestamp.toDate();
            }
        } else {
            return ventaModificada; // No hay fecha, no actualizar
        }

        // Calcular días hábiles transcurridos (excluyendo domingos)
        const diasHabilesTranscurridos = calcularDiasHabiles(fechaUltimaActualizacion, ahora);

        // Si ha pasado al menos 1 día hábil, avanzar al siguiente estado
        if (diasHabilesTranscurridos >= 1) {
            const siguienteEstado = obtenerSiguienteEstado(estadoActual);
            
            if (siguienteEstado) {
                const ventaActualizada: Venta = {
                    ...ventaModificada,
                    estadoPedido: siguienteEstado,
                    timestampActualizacion: Timestamp.now()
                };
                
                recalculateSaleFinances(ventaActualizada);
                ventasActualizadas++;
                hayCambios = true;
                
                console.log(`[VentasService] Estado actualizado automáticamente: ${venta.id} de "${estadoActual}" a "${siguienteEstado}" (${diasHabilesTranscurridos} días hábiles)`);
                
                return ventaActualizada;
            }
        }

        return ventaModificada;
    });

    // Solo actualizar en Firestore si hubo cambios
    if (hayCambios) {
        await updateDoc(mainDataRef, { ventasData: newVentasArray });
        
        // 🔄 SEÑAL: Marcar inventario para actualización
        console.log('[VentasService] 🔄 Marcando inventario para actualización después de actualización automática de estados');
        publicInventarioStore.markForUpdate();

        // 🔄 RECARGA: Recargar store de ventas
        console.log('[VentasService] 🔄 Recargando store de ventas después de actualización automática de estados');
        await ventasStore.reload();
    }

    return ventasActualizadas;
}

/**
 * Busca un cliente por teléfono. Si no existe, lo crea.
 * @param {string} userId - ID del usuario.
 * @param {string} nombreCompleto - Nombre del cliente.
 * @param {string} telefono - Teléfono del cliente.
 * @param {Cliente[]} clientesActuales - El array de clientes para buscar.
 * @param {UserProfile} currentUserProfile - Datos del usuario actual para el campo 'propietario'.
 * @returns {Promise<string | null>} El ID del cliente encontrado o creado, o null en caso de error.
 */
export async function findOrCreateCliente(userId: string, nombreCompleto: string, telefono: string, clientesActuales: Cliente[], currentUserProfile: UserProfile): Promise<string | null> {
    // Busca en la lista local de clientes para evitar lecturas innecesarias.
    const clienteExistente = clientesActuales.find(c =>
        c.contactos?.some(con => con.telefonos?.includes(telefono))
    );

    if (clienteExistente) {
        return clienteExistente.id;
    }

    const nuevoClienteId = generateId('cl');
    const clientesRef = collection(db, `${COLECCIONES.USUARIOS}/${userId}/${COLECCIONES.CLIENTES}`);

    const [nombre, ...apellidoArray] = nombreCompleto.split(' ');
    const apellido = apellidoArray.join(' ');

    const nuevoClienteData: Omit<Cliente, 'id'> = {
        tipoCliente: 'cliente',
        estadoCliente: 'Cliente Activo',
        fotoURL: '',
        contactos: [{
            nombre: nombre || '', apellido: apellido || '', telefonos: [telefono],
            email: null, cargo: null, direccion: null, profesion: null,
            facebookURL: null, instagramURL: null, tiktokURL: null, esPrincipal: true
        }],
        datosEntidad: null, etiquetas: [], archivosAdjuntos: [],
        propietarioId: userId,
        propietarioNombre: `${currentUserProfile.nombre || ''} ${currentUserProfile.apellido || ''}`.trim() || currentUserProfile.email,
        fechaIngreso: serverTimestamp(),
        terminosBusqueda: [],
        timestampCreacion: serverTimestamp(),
        timestampActualizacion: serverTimestamp()
    };

    try {
        await setDoc(doc(clientesRef, nuevoClienteId), nuevoClienteData);
        return nuevoClienteId;
    } catch (error) {
        console.error("Error crítico al crear nuevo cliente:", error);
        return null;
    }
}

/**
 * Registra una nueva actividad en el timeline de un cliente.
 * @param {string} userId - ID del usuario.
 * @param {string} clienteId - ID del cliente.
 * @param {Partial<ActividadCliente>} actividadData - Datos de la actividad.
 * @returns {Promise<void>}
 */
export async function registrarActividadCliente(userId: string, clienteId: string, actividadData: Partial<ActividadCliente>): Promise<void> {
    if (!clienteId) {
        console.warn("Se intentó registrar actividad sin un clienteId.");
        return;
    }
    const actividadesRef = collection(db, `${COLECCIONES.USUARIOS}/${userId}/${COLECCIONES.CLIENTES}/${clienteId}/${COLECCIONES.ACTIVIDADES_CLIENTE}`);

    const dataToSave: Omit<ActividadCliente, 'id'> = {
        contenido: actividadData.contenido || 'Actividad no especificada',
        creadorId: userId,
        creadorNombre: actividadData.creadorNombre || 'Sistema',
        tipoActividad: actividadData.tipoActividad || 'general',
        timestampCreacion: serverTimestamp(),
        ...actividadData
    };

    await addDoc(actividadesRef, dataToSave);
}

export async function crearCliente(userId: string, clienteData: Partial<Cliente>): Promise<string> {
    const clientesRef = collection(db, `${COLECCIONES.USUARIOS}/${userId}/${COLECCIONES.CLIENTES}`);
    const docRef = await addDoc(clientesRef, clienteData);
    return docRef.id;
}
